import React, { useEffect, useState } from "react";

import { I18nextProvider } from "react-i18next";

import i18n from "./providers/i18n";

import AppRouter from "./Router";

import { usePartnerConfig } from "./hooks/strapi/usePartnerConfig";
import { usePageConfig } from "./hooks/strapi/usePageConfig";

import { partnerService } from "./services/partnerService";

import { mergePartnerPageConfigWithPageConfig } from "./helpers/mergePartnerPageConfigWithPageConfig";

import { datadogRum } from "@datadog/browser-rum";

// Initialize Datadog RUM once at module load — outside the component so
// React StrictMode double-invoking effects never causes a second init.
datadogRum.init({
  applicationId: "483e6f1b-2747-4ada-8986-8ba4211fc784",
  clientToken: "pub0205d538e78d9413594e99c239555e7f",
  site: "ap1.datadoghq.com",
  service: "virtualhospital",
  env: "uat",
  version: "2.0.0",
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  defaultPrivacyLevel: "mask-user-input",
});

function App() {
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [i18nReady, setI18nReady] = useState(false);

  // Wait for i18n to initialize
  useEffect(() => {
    if (i18n.isInitialized) {
      setI18nReady(true);
    } else {
      const handleInit = () => setI18nReady(true);
      i18n.on("initialized", handleInit);
      return () => {
        i18n.off("initialized", handleInit);
      };
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const partnerFromUrl = urlParams.get("partner");
      const existingPartner = partnerService.getPartner();

      // If we already have a partner AND no new partner params in URL → reuse existing
      if (existingPartner && !partnerFromUrl) {
        console.log(
          `[App] Using existing partner from storage: ${existingPartner.name}`
        );
        setPartnerId(existingPartner.name);
        setIsReady(true);
        return;
      }

      // Otherwise → initialize new partner
      console.log(`[App] Initializing partner from URL`);
      const newPartner = await partnerService.initPartner(window.location);
      setPartnerId(newPartner?.name || null);
      setIsReady(true);
    };

    init();
  }, []);

  // Don't call hook with "Webview" ID
  const safePartnerId = partnerId === "Webview" ? "" : partnerId;
  const partnerConfig = usePartnerConfig(safePartnerId);
  const pageConfigs = usePageConfig();

  useEffect(() => {
    if (!pageConfigs) return;

    sessionStorage.setItem("partnerConfig", JSON.stringify(partnerConfig));

    const configs = mergePartnerPageConfigWithPageConfig({
      pageConfigs,
      partnerPageConfigs: partnerConfig?.partnerPageConfigs,
      enabledPages: partnerConfig?.enabledPages,
    });
    sessionStorage.setItem("pageConfigs", JSON.stringify(configs));
  }, [partnerConfig, pageConfigs]);

  // Wait for both partner and i18n readiness
  if (!isReady || !i18nReady) return null;

  return (
    <I18nextProvider i18n={i18n}>
      <AppRouter
        config={partnerId && partnerId !== "Webview" ? partnerConfig : null}
      />
    </I18nextProvider>
  );
}

export default App;
